import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const App = () => {
  const [robots, setRobots] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState({});
  const [errors, setErrors] = useState({});
  const [showErrorPopup, setShowErrorPopup] = useState(null);
  const [runningJobs, setRunningJobs] = useState([]);
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const authAxios = axios.create({
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    console.log('Token found:', token);
    authAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    authAxios.get('/api/test/getall')
      .then(res => setRobots(res.data))
      .catch(err => console.error('Error fetching robots:', err));

    authAxios.get('/api/config/getall')
      .then(res => {
        const obj = {};
        res.data.forEach(cfg => {
          obj[cfg.robotCategory] = cfg;
        });
        setConfigs(obj);
      })
      .catch(err => console.error('Error fetching configs:', err));

    authAxios.get('/api/config/runningJobs')
      .then(res => {
        console.log('Running Jobs:', res.data);
        setRunningJobs(res.data);
      })
      .catch(err => console.error('Error fetching running jobs:', err));
  }, []);

  useEffect(() => {
    // Fetch errors whenever robots change
    robots.forEach(robot => {
      fetchErrors(robot.name);
    });
  }, [robots]);

  const runRobot = async (robotIndex) => {
    setLoading(true);
    try {
      const response = await authAxios.post(`/api/test/${robotIndex}/run`);
      setResults(prev => ({ ...prev, [robotIndex]: response.data }));
    } catch (error) {
      console.error(`Error running robot ${robotIndex}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const runAllRobots = async () => {
    setLoading(true);
    try {
      const response = await authAxios.post('/api/test/run-all');
      setResults(response.data);
    } catch (error) {
      console.error('Error running all robots:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchErrors = async (robotName) => {
    try {
      console.log(`Fetching errors for ${robotName}`);
      const res = await authAxios.get(`/api/test/${robotName}/errors`);
      setErrors(prev => ({ ...prev, [robotName]: res.data }));
    } catch (err) {
      console.error(`Error fetching errors for ${robotName}:`, err);
    }
  };

  const deleteErrors = async (robotName) => {
    try {
      await authAxios.delete(`/api/test/${robotName}/errors`);
      setErrors(prev => ({ ...prev, [robotName]: [] }));
      setShowErrorPopup(null);
    } catch (err) {
      console.error(`Error deleting errors for ${robotName}:`, err);
    }
  };

  const updateConfig = async (category) => {
    const original = configs[category];
    const rawHours = original.runHours;

    const parsedHours = typeof rawHours === 'string'
      ? rawHours
          .split(' ')
          .map(h => h.trim())
          .filter((h, i, arr) => {
            const parts = h.split(':');
            if (parts.length === 1) {
              const hour = parseInt(parts[0]);
              return !isNaN(hour) && hour >= 0 && hour <= 24 && arr.indexOf(h) === i;
            } else if (parts.length === 2) {
              const hour = parseInt(parts[0]);
              const min = parseInt(parts[1]);
              return !isNaN(hour) && !isNaN(min)
                && hour >= 0 && hour <= 24
                && min >= 0 && min < 60
                && arr.indexOf(h) === i;
            }
            return false;
          })
      : [];

    const updated = {
      ...original,
      runHours: parsedHours,
    };

    try {
      await authAxios.post('/api/config/update', updated);
      alert('Config updated');
    } catch (err) {
      alert('Failed to update config');
    }
  };

  const handleChange = (category, field, value) => {
    setConfigs(prev => {
      const updatedValue = field === 'runHours'
        ? value // păstrăm ca string
        : value;

      return {
        ...prev,
        [category]: {
          ...prev[category],
          [field]: updatedValue,
        }
      };
    });
  };

  const handleBulkChange = (field, value) => {
    setConfigs(prev => {
      const updatedConfigs = { ...prev };
      Object.keys(updatedConfigs).forEach(category => {
        updatedConfigs[category] = {
          ...updatedConfigs[category],
          [field]: field === 'runHours' ? value.split(' ') : value
        };
      });
      return updatedConfigs;
    });
  };

  const updateAllConfigs = async () => {
    try {
      const updatedConfigs = Object.values(configs);
      await Promise.all(updatedConfigs.map(config => authAxios.post('/api/config/update', config)));
      alert('All configs updated');
    } catch (err) {
      alert('Failed to update all configs');
    }
  };

  const exportRobotsToTxt = () => {
    const content = robots.map(robot => `${robot.name} - ${robot.source}`).join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'robots_sources.txt';
    link.click();
  };

  return (
    <div className="p-6 font-sans bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-center text-gray-800">🤖 Euro Scraper Dashboard</h1>

      {/* <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4">Running Jobs</h2>
        <ul className="list-disc pl-6">
          {runningJobs.map(job => (
            <li key={job.robotCategory}>
              <strong>{job.robotCategory}</strong>: Last Run At: {job.lastRunAt}, Next Run At: {job.nextRunAt}
            </li>
          ))}
        </ul>
      </div> */}

      <div className="flex justify-center mb-6">
        <button
          onClick={runAllRobots}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded shadow"
        >
          {loading ? 'Running All...' : 'Run All Robots'}
        </button>
      </div>

      <div className="flex justify-center mb-6">
        <button
          onClick={exportRobotsToTxt}
          className="bg-gray-800 hover:bg-gray-900 text-white px-6 py-2 rounded shadow"
        >
          Export Robots to TXT
        </button>
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4">Bulk Configuration</h2>
        <div className="space-y-4">
          <div>
            <label className="block font-medium">Ore (ex: 8, 14, 22)</label>
            <input
              type="text"
              className="border p-1 w-full rounded"
              onChange={e => handleBulkChange('runHours', e.target.value)}
            />
          </div>

          <div>
            <label className="block font-medium">Target API</label>
            <input
              type="text"
              className="border p-1 w-full rounded"
              onChange={e => handleBulkChange('targetApi', e.target.value)}
            />
          </div>

          <button
            onClick={updateAllConfigs}
            className="bg-gray-800 hover:bg-gray-900 text-white px-3 py-1 rounded"
          >
            Update All Configs
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {robots.map(robot => {
          const conf = configs[robot.name] || { runHours: [], targetApi: '', active: false };
          const result = results[robot.index];
          const robotErrors = errors[robot.name] || [];

          return (
            <div key={robot.index} className="bg-white p-4 rounded-lg shadow border">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-semibold text-gray-700">{robot.name}</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={() => runRobot(robot.index)}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded"
                    disabled={loading}
                  >
                    {loading ? 'Running...' : 'Run'}
                  </button>
                  <a
                    href={`${robot.source}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                  >
                    Source
                  </a>
                </div>
              </div>

              {robotErrors.length > 0 && (
                <button
                  onClick={() => setShowErrorPopup(robot.name)}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded mb-3"
                >
                  Show Errors
                </button>
              )}

              {showErrorPopup === robot.name && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
                  <div className="bg-white p-6 rounded shadow-lg w-96">
                    <h3 className="text-lg font-bold mb-4">Errors for {robot.name}</h3>
                    <ul className="list-disc pl-5 mb-4">
                      {robotErrors.map((error, index) => (
                        <li key={index} className="text-sm text-gray-700">{error.errorMessage}</li>
                      ))}
                    </ul>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => deleteErrors(robot.name)}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                      >
                        Delete Errors
                      </button>
                      <button
                        onClick={() => setShowErrorPopup(null)}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2 text-sm">
                <div>
                  <label className="block font-medium">Ore (ex: 8, 14, 22)</label>
                  <input
                    type="text"
                    className="border p-1 w-full rounded"
                    value={Array.isArray(conf.runHours) ? conf.runHours.join(' ') : conf.runHours || ''}
                    onChange={e => handleChange(robot.name, 'runHours', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block font-medium">Target API</label>
                  <input
                    type="text"
                    className="border p-1 w-full rounded"
                    value={conf.targetApi || ''}
                    onChange={e => handleChange(robot.name, 'targetApi', e.target.value)}
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={conf.active}
                    onChange={e => handleChange(robot.name, 'active', e.target.checked)}
                  />
                  <span>Activ</span>
                </div>
              </div>

              <button
                onClick={() => updateConfig(robot.name)}
                className="mt-3 bg-gray-800 hover:bg-gray-900 text-white px-3 py-1 rounded"
              >
                Salvează Config
              </button>

              {result && (
                <div className="mt-4 bg-gray-100 border border-gray-300 rounded p-3 text-sm text-gray-800 overflow-auto max-h-60">
                  <div className="font-medium text-gray-600 mb-1">Rezultat:</div>
                  <pre className="whitespace-pre-wrap">{JSON.stringify(result, null, 2)}</pre>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default App;